import { useEffect, useRef } from 'react';
import { EditorState, Compartment } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLineGutter, ViewUpdate } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { oneDarkHighlightStyle } from '@codemirror/theme-one-dark';
import { indentUnit, syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language';
import { getLanguageConfig } from '../config/languages';
import type { LanguageId } from '../types/language';

interface UseCodeMirrorProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  code: string;
  onChange: (code: string) => void;
  onRun?: () => void;
  theme?: 'light' | 'dark';
  language?: LanguageId;
}

export function useCodeMirror({ 
  containerRef, 
  code, 
  onChange, 
  onRun, 
  theme = 'light',
  language = 'python'
}: UseCodeMirrorProps) {
  const viewRef = useRef<EditorView | null>(null);
  const highlightStyleCompartment = useRef(new Compartment());
  const languageCompartment = useRef(new Compartment());
  const indentCompartment = useRef(new Compartment());
  
  // Keep refs to callbacks to avoid stale closures in CodeMirror event handlers
  const onRunRef = useRef(onRun);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onRunRef.current = onRun;
    onChangeRef.current = onChange;
  }, [onRun, onChange]);

  // Initialize editor
  useEffect(() => {
    if (!containerRef.current) return;

    // Get language configuration
    const langConfig = getLanguageConfig(language);
    
    // Base theme for structural/layout styles
    const baseTheme = EditorView.theme({
      "&": { 
        height: "100%", 
        backgroundColor: "var(--background)",
        color: "var(--foreground)" 
      },
      ".cm-scroller": { overflow: "auto" },
      ".cm-gutters": { 
        backgroundColor: "var(--sidebar)",
        color: "var(--muted-foreground)",
        borderRight: "1px solid var(--border)"
      },
      ".cm-activeLineGutter": {
        backgroundColor: "var(--accent)"
      },
      ".cm-activeLine": {
         backgroundColor: "var(--accent)"
      },
      ".cm-panels": {
        backgroundColor: "var(--sidebar)",
        color: "var(--foreground)"
      },
      ".cm-content": {
        caretColor: "var(--cm-cursor)"
      },
      "&.cm-focused .cm-cursor, &.cm-focused .cm-dropCursor": {
        borderLeftColor: "var(--cm-cursor)"
      },
      "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection": {
        backgroundColor: "var(--cm-selection-bg)"
      }
    });

    const startState = EditorState.create({
      doc: code,
      extensions: [
        lineNumbers(),
        highlightActiveLineGutter(),
        history(),
        // Custom keybindings
        keymap.of([
          {
            key: 'Shift-Enter',
            run: () => {
              onRunRef.current?.();
              return true;
            }
          },
          indentWithTab,
          ...defaultKeymap,
          ...historyKeymap,
        ]),

        // Language & Indentation
        languageCompartment.current.of(langConfig.codeMirrorLang()),
        indentCompartment.current.of([
          indentUnit.of(langConfig.indentUnit),
          EditorState.tabSize.of(langConfig.indentUnit.length),
        ]),

        EditorView.lineWrapping,
        
        // Theme & Highlight Style
        baseTheme,
        
        // Syntax Highlighting
        highlightStyleCompartment.current.of(
          theme === 'dark' 
            ? syntaxHighlighting(oneDarkHighlightStyle) 
            : syntaxHighlighting(defaultHighlightStyle)
        ),

        EditorView.updateListener.of((update: ViewUpdate) => {
          if (update.docChanged) {
            onChangeRef.current?.(update.state.doc.toString());
          }
        })
      ]
    });

    const view = new EditorView({
      state: startState,
      parent: containerRef.current
    });

    viewRef.current = view;

    return () => {
      view.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Update theme (syntax highlighting)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    view.dispatch({
      effects: [
        highlightStyleCompartment.current.reconfigure(
          theme === 'dark' 
            ? syntaxHighlighting(oneDarkHighlightStyle) 
            : syntaxHighlighting(defaultHighlightStyle)
        )
      ]
    });
  }, [theme]);

  // Update language (only if editor is already initialized)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const langConfig = getLanguageConfig(language);
    view.dispatch({
      effects: [
        languageCompartment.current.reconfigure(langConfig.codeMirrorLang()),
        indentCompartment.current.reconfigure([
          indentUnit.of(langConfig.indentUnit),
          EditorState.tabSize.of(langConfig.indentUnit.length),
        ])
      ]
    });
  }, [language]);

  // Update content when code prop changes (externally)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const currentCode = view.state.doc.toString();
    if (currentCode !== code) {
      view.dispatch({
        changes: {
          from: 0,
          to: currentCode.length,
          insert: code
        }
      });
    }
  }, [code]);

  return viewRef;
}
